import { createInteractionTool, } from '@debbie/common/dist/agents/tools/create-interaction.js';
import { Entity } from '@debbie/common/dist/index.js';
import { interactionBody, interactionSubject, } from '@debbie/common/dist/interactions/extract.js';
import { filterPropertyTypes } from '@debbie/common/dist/properties/index.js';
import { TemplateMention, TemplateType, } from '@debbie/common/dist/templates/index.js';
import { getMentionSuggestions, toEditorMentions, } from '@debbie/common/dist/templates/suggestions.js';
import { looksLikeHtml, plaintextToHtml, } from '@debbie/common/dist/util/html.js';
import { useCase } from '@debbie/cortex/dist/cases/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useInteractionsByReference } from '@debbie/cortex/dist/interactions/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { useMentionSuggestions } from '@debbie/cortex/dist/templates/index.js';
import { useTenantLoadable } from '@debbie/cortex/dist/tenant/index.js';
import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { encodeText, getSubjectContent, parseText, } from '../../utils/templates.js';
import NewEditor from '../editor/Editor.js';
import { htmlToPlainText } from '../editor/utils.js';
import Errors from '../Errors.js';
import File from '../File/index.js';
import FileDropzone from '../FileDropzone.js';
import Interaction from '../Interaction/Interaction.js';
import Modal from '../Modal.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import { EditError, useProposalEdits } from './shared.js';
const Subject = styled.div `
  font-weight: 600;
  font-size: 0.95em;
  color: ${THEME.COLOR_PRIMARY};
  margin-bottom: 8px;
  line-height: 1.4;
`;
const BodyBox = styled.div `
  background: #fff;
  border: 1px solid ${THEME.BORDER};
  border-radius: 3px;
  padding: 12px 14px;
  font-size: 0.95em;
  line-height: 1.6;
  word-break: break-word;
  max-height: 400px;
  overflow: auto;
  color: ${THEME.COLOR_PRIMARY};
`;
const PlaintextBody = styled.div `
  white-space: pre-wrap;
`;
const HtmlBody = styled.div `
  p {
    margin: 0 0 0.6em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  ul,
  ol {
    margin: 0.4em 0;
    padding-left: 1.4em;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.8em 0 0.4em 0;
  }
`;
const Attachments = styled.div `
  margin-top: 8px;
  font-size: 0.8em;
  color: ${THEME.COLOR_GREY};
`;
const ReplyToBox = styled.div `
  padding: 8px 10px;
  border: 1px solid ${THEME.BORDER_STRONG};
  background: #fff;
  border-radius: 3px;
  font-size: 0.8em;
  cursor: pointer;
`;
const ReplyToLabel = styled.div `
  font-size: 0.8em;
  color: ${THEME.COLOR_GREY};
`;
const ReplyToSubject = styled.div `
  color: ${THEME.COLOR_PRIMARY};
  font-weight: bold;
`;
const ReplyToSnippet = styled.div `
  color: ${THEME.COLOR_GREY};
  line-height: 1.5;
  word-break: break-word;
`;
const MentionChip = styled.span `
  display: inline-block;
  padding: 0 6px;
  border-radius: 10px;
  background: ${THEME.BACKGROUND};
  color: ${THEME.COLOR_ACCENT};
  border: 1px solid ${THEME.BORDER};
  font-size: 0.92em;
  font-weight: 500;
  vertical-align: baseline;
  white-space: nowrap;
`;
const MENTION_RE = /\{{2,3}\s*([^}]+?)\s*\}{2,3}/g;
const TextWithMentions = ({ text, handleToLabel }) => {
    const parts = [];
    let lastIndex = 0;
    let key = 0;
    for (const match of text.matchAll(MENTION_RE)) {
        const start = match.index ?? 0;
        if (start > lastIndex) {
            parts.push(text.slice(lastIndex, start));
        }
        const handle = match[0];
        const label = handleToLabel.get(handle);
        if (label) {
            parts.push(React.createElement(MentionChip, { key: key++, title: handle },
                "@",
                label));
        }
        else {
            parts.push(handle);
        }
        lastIndex = start + handle.length;
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }
    return React.createElement(React.Fragment, null, parts);
};
const ALLOWED_TAGS = new Set([
    'p',
    'br',
    'hr',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'pre',
    'code',
    'table',
    'thead',
    'tbody',
    'tr',
    'td',
    'th',
    'span',
    'div',
]);
const renderNode = (node, key, handleToLabel) => {
    if (node.nodeType === 3 /* TEXT_NODE */) {
        return (React.createElement(TextWithMentions, { key: key, text: node.textContent ?? '', handleToLabel: handleToLabel }));
    }
    if (node.nodeType !== 1 /* ELEMENT_NODE */) {
        return null;
    }
    const el = node;
    const tag = el.tagName.toLowerCase();
    const children = Array.from(el.childNodes).map((child, i) => renderNode(child, `${key}-${i}`, handleToLabel));
    if (!ALLOWED_TAGS.has(tag)) {
        return React.createElement(React.Fragment, { key: key }, children);
    }
    if (tag === 'br' || tag === 'hr') {
        return React.createElement(tag, { key });
    }
    return React.createElement(tag, { key }, children);
};
export const RenderedHtmlBody = ({ html, handleToLabel }) => {
    // <div> wrapper gives DOMParser a single root to walk.
    const root = useMemo(() => {
        const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
        return doc.body.firstChild;
    }, [html]);
    if (!root) {
        return null;
    }
    return (React.createElement(HtmlBody, null, Array.from(root.childNodes).map((child, i) => renderNode(child, `${i}`, handleToLabel))));
};
export const useHandleToLabel = (caseId) => {
    const mentions = useSelector((s) => s.lang.mentions);
    const caseLoadable = useCase(caseId);
    const singleCase = h.hasData(caseLoadable) ? caseLoadable.data : null;
    const suggestions = useMentionSuggestions(TemplateType.EMAIL, singleCase?.caseGroup?.id ?? null);
    return useMemo(() => {
        const map = new Map();
        for (const key of Object.keys(TemplateMention)) {
            const handle = TemplateMention[key];
            const label = mentions[handle];
            if (label) {
                map.set(handle, label);
            }
        }
        for (const suggestion of suggestions) {
            map.set(suggestion.field, suggestion.label);
        }
        return map;
    }, [mentions, suggestions]);
};
const ReplyToPreview = ({ caseId, interactionId }) => {
    const lang = useSelector((s) => s.lang);
    const loadable = useInteractionsByReference(Entity.Case, caseId);
    const [open, setOpen] = useState(false);
    if (h.isError(loadable)) {
        return React.createElement(Errors, { errors: [loadable.error.message] });
    }
    if (!h.hasData(loadable)) {
        return React.createElement(PlaceholderLoader, null);
    }
    const interaction = loadable.data.find((i) => i.id === interactionId);
    if (!interaction) {
        return null;
    }
    const subject = interactionSubject(interaction);
    const flattened = interactionBody(interaction).replace(/\s+/g, ' ').trim();
    const snippet = flattened.length > 100 ? `${flattened.slice(0, 100)}…` : flattened;
    return (React.createElement("div", null,
        React.createElement(ReplyToLabel, null, lang.agentProposalBodies.replyingTo),
        React.createElement(ReplyToBox, { onClick: () => setOpen(true) },
            subject && React.createElement(ReplyToSubject, null, subject),
            snippet && React.createElement(ReplyToSnippet, null, snippet)),
        open && (React.createElement(Modal, { header: subject ?? lang.agentProposalBodies.replyingTo, onToggle: () => setOpen(false), maxWidth: "900px" },
            React.createElement(Interaction, { interaction: interaction, alwaysExtended: true, notActionable: true })))));
};
export const useCaseFiles = (caseId) => {
    const documentsLoadable = useDocumentsByReference(caseId);
    const interactionsLoadable = useInteractionsByReference(Entity.Case, caseId);
    const byId = new Map();
    if (h.hasData(documentsLoadable)) {
        for (const document of documentsLoadable.data) {
            if (document.file?.fileId && document.file.fileName) {
                byId.set(document.file.fileId, {
                    fileId: document.file.fileId,
                    fileName: document.file.fileName,
                    contentType: document.file.contentType ?? '',
                });
            }
        }
    }
    if (h.hasData(interactionsLoadable)) {
        for (const interaction of interactionsLoadable.data) {
            for (const attachment of interaction.attachments ?? []) {
                if (attachment.fileId && attachment.fileName) {
                    byId.set(attachment.fileId, {
                        fileId: attachment.fileId,
                        fileName: attachment.fileName,
                        contentType: attachment.contentType ?? '',
                    });
                }
            }
        }
    }
    const errors = [
        h.isError(documentsLoadable) ? documentsLoadable.error.message : null,
        h.isError(interactionsLoadable) ? interactionsLoadable.error.message : null,
    ].filter((message) => message != null);
    return { files: Array.from(byId.values()), byId, errors };
};
export const attachmentFile = (byId, fileId) => byId.get(fileId) ?? {
    fileId,
    fileName: `${fileId.slice(0, 8)}…`,
    contentType: '',
};
export const AttachmentList = ({ caseId, attachments }) => {
    const { byId, errors } = useCaseFiles(caseId);
    return (React.createElement("div", null,
        errors.length > 0 && React.createElement(Errors, { errors: errors }),
        attachments.map((a) => (React.createElement(File, { key: a.fileId, file: attachmentFile(byId, a.fileId) })))));
};
export const AddAttachmentChip = styled.div `
  display: inline-flex;
  background: ${THEME.FILL};
  padding: 5px 15px;
  font-size: 12px;
  border-radius: 15px;
  margin-right: 8px;
  margin-bottom: 10px;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  user-select: none;
`;
// The Editor fires onChange once on mount with its tiptap-normalized seed; that first value is the edit baseline, so normalization doesn't count as a reviewer edit.
const EditableInteractionFields = ({ input, caseId, onEditsChange }) => {
    const lang = useSelector((s) => s.lang);
    const caseLoadable = useCase(caseId);
    const singleCase = h.hasData(caseLoadable) ? caseLoadable.data : null;
    const tenantLoadable = useTenantLoadable();
    const tenant = h.hasData(tenantLoadable) ? tenantLoadable.data : null;
    const propertyTypesLoadable = usePropertyTypes();
    const subjectBaseline = useRef(null);
    const bodyBaseline = useRef(null);
    const { patch, editError, emit } = useProposalEdits(createInteractionTool, input, onEditsChange);
    const [attachments, setAttachments] = useState(input.attachments ?? []);
    // Ref so async dropzone callbacks don't read stale attachments.
    const attachmentsRef = useRef(attachments);
    attachmentsRef.current = attachments;
    const [uploadedById, setUploadedById] = useState(new Map());
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const dropzoneRef = useRef(null);
    const caseFiles = useCaseFiles(caseId);
    if (h.isError(caseLoadable)) {
        return React.createElement(Errors, { errors: [caseLoadable.error.message] });
    }
    if (h.isError(propertyTypesLoadable)) {
        return React.createElement(Errors, { errors: [propertyTypesLoadable.error.message] });
    }
    if (h.isError(tenantLoadable)) {
        return React.createElement(Errors, { errors: [tenantLoadable.error.message] });
    }
    // Mounting before these settle would later re-seed the editor and discard the reviewer's typing.
    if (singleCase == null ||
        tenant == null ||
        !h.hasData(propertyTypesLoadable)) {
        return React.createElement(PlaceholderLoader, null);
    }
    const mentionSuggestions = getMentionSuggestions(lang, TemplateType.EMAIL, filterPropertyTypes(propertyTypesLoadable.data, Entity.Case, singleCase.caseGroup?.id || null).concat(filterPropertyTypes(propertyTypesLoadable.data, Entity.Customer), filterPropertyTypes(propertyTypesLoadable.data, Entity.Creditor)), tenant);
    const editorMentions = toEditorMentions(mentionSuggestions);
    const subjectSeed = encodeText(input.subject ?? '', mentionSuggestions);
    const bodySeed = encodeText(looksLikeHtml(input.body) ? input.body : plaintextToHtml(input.body), mentionSuggestions);
    const onSubjectChange = (html) => {
        const value = getSubjectContent(htmlToPlainText(parseText(html, mentionSuggestions)));
        if (subjectBaseline.current === null) {
            subjectBaseline.current = value;
            return;
        }
        if (value === subjectBaseline.current) {
            delete patch.current.subject;
        }
        else {
            patch.current.subject = value;
        }
        emit();
    };
    const onBodyChange = (html) => {
        const value = parseText(html, mentionSuggestions);
        if (bodyBaseline.current === null) {
            bodyBaseline.current = value;
            return;
        }
        if (value === bodyBaseline.current) {
            delete patch.current.body;
        }
        else {
            patch.current.body = value;
        }
        emit();
    };
    const onAttachmentsChange = (next) => {
        setAttachments(next);
        const baseline = (input.attachments ?? [])
            .map((a) => a.fileId)
            .sort()
            .join(',');
        const current = next
            .map((a) => a.fileId)
            .sort()
            .join(',');
        if (current === baseline) {
            delete patch.current.attachments;
        }
        else {
            patch.current.attachments = next;
        }
        emit();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(NewEditor, { hideControls: true, compact: true, singleLine: true, placeholder: lang.interactions.subject, mentions: editorMentions, initialContent: subjectSeed, onChange: onSubjectChange }),
        React.createElement(NewEditor, { mentions: editorMentions, initialContent: bodySeed, onChange: onBodyChange }),
        React.createElement(FileDropzone, { dropzoneRef: dropzoneRef, setLoading: (loading) => {
                setUploadingAttachment(loading);
                if (loading) {
                    const edits = { ...patch.current };
                    onEditsChange({
                        edits: Object.keys(edits).length > 0 ? edits : null,
                        isValid: false,
                    });
                }
                else {
                    emit();
                }
            }, onChange: (file) => {
                setUploadedById((prev) => new Map(prev).set(file.fileId, {
                    fileId: file.fileId,
                    fileName: file.fileName ?? `${file.fileId.slice(0, 8)}…`,
                    contentType: file.contentType ?? '',
                }));
                onAttachmentsChange([
                    ...attachmentsRef.current,
                    { fileId: file.fileId },
                ]);
            } },
            React.createElement("div", null,
                attachments.map((a) => (React.createElement(File, { key: a.fileId, file: uploadedById.get(a.fileId) ??
                        attachmentFile(caseFiles.byId, a.fileId), removable: !uploadingAttachment, onRemove: () => onAttachmentsChange(attachments.filter((x) => x.fileId !== a.fileId)) }))),
                React.createElement(AddAttachmentChip, { $disabled: uploadingAttachment, onClick: () => {
                        if (uploadingAttachment) {
                            return;
                        }
                        // @ts-ignore — dropzone exposes open() via the ref
                        dropzoneRef.current?.open();
                    } },
                    "+ ",
                    lang.interactions.addFiles))),
        caseFiles.errors.length > 0 && React.createElement(Errors, { errors: caseFiles.errors }),
        editError && (React.createElement(EditError, null,
            lang.agentProposals.invalidEdits,
            ": ",
            editError))));
};
const CreateInteractionBody = ({ input, caseId, onEditsChange }) => {
    const lang = useSelector((s) => s.lang);
    const handleToLabel = useHandleToLabel(caseId);
    const attachmentCount = input.attachments?.length ?? 0;
    const bodyIsHtml = input.body ? looksLikeHtml(input.body) : false;
    return (React.createElement(Wrapper, null,
        onEditsChange != null ? (React.createElement(EditableInteractionFields, { input: input, caseId: caseId, onEditsChange: onEditsChange })) : (React.createElement(React.Fragment, null,
            input.subject && (React.createElement(Subject, null,
                React.createElement(TextWithMentions, { text: input.subject, handleToLabel: handleToLabel }))),
            input.body && (React.createElement(BodyBox, null, bodyIsHtml ? (React.createElement(RenderedHtmlBody, { html: input.body, handleToLabel: handleToLabel })) : (React.createElement(PlaintextBody, null,
                React.createElement(TextWithMentions, { text: input.body, handleToLabel: handleToLabel }))))))),
        onEditsChange == null && attachmentCount > 0 && (React.createElement(AttachmentList, { caseId: caseId, attachments: input.attachments ?? [] })),
        input.inReplyToInteractionId && (React.createElement(ReplyToPreview, { caseId: caseId, interactionId: input.inReplyToInteractionId }))));
};
const Wrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
export default CreateInteractionBody;
//# sourceMappingURL=CreateInteractionBody.js.map